/**
 * FikoKenarIslem.ts — 3D KENAR İŞLEMLERİ: Fillet (yuvarlatma) + Chamfer (pah)
 *
 * Mesh tabanlı yaklaşım: gövdenin keskin (>eşik°) yüz-geçiş kenarlarını bulur,
 * her kenar boyunca bir kesme/ekleme aleti (prizma) süpürür ve CSG boolean ile
 * gövdeyi DÖNÜŞTÜRÜR. Post-işlem feature'ıdır (mirror gibi) → rebuild()'de gövde
 * kurulduktan SONRA uygulanır. ham-boolean + tek finalize deseni (donma yok).
 *
 * DÜRÜST SINIR: yarım-kenar topolojisi olmadığından köşe buluşmaları tam mitre
 * değildir (CSG watertight tutar ama köşe yaklaşık). Dışbükey kenarlarda (kutu/
 * pad/primitif) sonuç temiz. Çok yoğun kenarlı gövdede yavaş olabilir.
 */

import * as THREE from 'three';
import { FikoKatiMotoru } from './FikoKatiMotoru';
import { ITopoImza, topoEslesirMi } from './fiko-toporef';

interface IKenarSeg {
  a: THREE.Vector3; b: THREE.Vector3;
  n1: THREE.Vector3; n2: THREE.Vector3;
  disbukey: boolean;
}

export class FikoKenarIslem {
  constructor(private kati: FikoKatiMotoru) {}

  /**
   * Gövdeye fillet/chamfer uygular → HAM geometri (çağıran finalize eder). Başarısızsa null.
   * seciliNoktalar verilirse (CATIA gibi) SADECE o kenarlara uygular — segment orta noktası
   * bir seçili noktaya `tol` mm içindeyse o kenar dahil. Boş/undefined → TÜM keskin kenarlar.
   */
  uygula(geo: THREE.BufferGeometry, tip: 'fillet' | 'chamfer', mesafe = 4, aciDeg = 30, seciliNoktalar?: THREE.Vector3[], tol = 1.5, seciliImza?: ITopoImza[]): THREE.BufferGeometry | null {
    if (!(mesafe > 0.01)) return null;
    let segler = this.kenarlariCikar(geo, aciDeg);
    if (!segler.length) return null;
    // SEÇİME GÖRE FİLTRE (CATIA): seciliImza varsa B-Rep topo-eşleştirme (merkez+yön+uzunluk skoru) →
    // parametre değişse de doğru kenar bulunur. Yoksa eski konumsal (midpoint ≤ tol) eşleştirme.
    // (boş dizi = henüz kenar seçilmedi → hiçbir kenara uygulama). undefined = tüm kenarlar.
    if (seciliImza !== undefined && seciliImza.length) {
      const olcek = this.modelOlcek(geo);
      segler = segler.filter(s => topoEslesirMi(seciliImza, this.segImza(s), olcek));   // çoka-bir: tüm eşleşen segmentler
      if (!segler.length) return null;
    } else if (seciliNoktalar !== undefined) {
      if (!seciliNoktalar.length) return null;
      const tol2 = tol * tol; const mid = new THREE.Vector3();
      segler = segler.filter(s => {
        mid.addVectors(s.a, s.b).multiplyScalar(0.5);
        for (const p of seciliNoktalar) if (mid.distanceToSquared(p) <= tol2) return true;
        return false;
      });
      if (!segler.length) return null;
    }
    // çok yoğun kenarda (performans) sınırla
    if (segler.length > 400) return null;

    let enKisa = Infinity;
    for (const s of segler) enKisa = Math.min(enKisa, s.a.distanceTo(s.b));
    const r = Math.min(mesafe, Math.max(0.05, enKisa * 0.49));

    let g: THREE.BufferGeometry = geo.clone();
    let ilk = true;
    for (const s of segler) {
      let alet: THREE.BufferGeometry | null = null;
      try {
        // chamfer → üçgen pah; fillet → köşe-yay kesiti (TEK temiz çıkarma, artık parça YOK)
        alet = tip === 'chamfer' ? this.pahAleti(s, r) : this.filletKesit(s, r);
        if (!alet) continue;
        const r2 = s.disbukey ? this.kati.cikarHam(g, alet) : this.kati.birlestirHam(g, alet);
        if (!ilk) g.dispose();
        g = r2; ilk = false; alet.dispose(); alet = null;
      } catch (e) {
        alet?.dispose();
        console.warn('[KenarIslem] kenar atlandı', e);
      }
    }
    return ilk ? null : g;
  }

  /** Bir kenar segmentinin topo imzası: merkez + yön + uzunluk (stored imza ile aynı format). */
  private segImza(s: IKenarSeg): ITopoImza {
    const dx = s.b.x - s.a.x, dy = s.b.y - s.a.y, dz = s.b.z - s.a.z;
    const uz = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
    return { merkez: [(s.a.x + s.b.x) / 2, (s.a.y + s.b.y) / 2, (s.a.z + s.b.z) / 2], yon: [dx / uz, dy / uz, dz / uz], olcu: uz };
  }

  /** Model köşegeni (tolerans normalizasyonu için ölçek). */
  private modelOlcek(geo: THREE.BufferGeometry): number {
    if (!geo.boundingBox) geo.computeBoundingBox();
    const bb = geo.boundingBox; if (!bb) return 1;
    return Math.max(1e-3, bb.min.distanceTo(bb.max));
  }

  private kenarlariCikar(geo: THREE.BufferGeometry, aciDeg: number): IKenarSeg[] {
    const pos = geo.attributes['position'] as THREE.BufferAttribute;
    if (!pos) return [];
    const idx = geo.index;
    const ucgen = idx ? idx.count / 3 : pos.count / 3;
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    const e1 = new THREE.Vector3(), e2 = new THREE.Vector3(), n = new THREE.Vector3();
    const Q = 1e-3;
    const q = (v: number) => Math.round(v / Q);
    const cosThr = Math.cos(aciDeg * Math.PI / 180);

    interface IKayit { ax: number; ay: number; az: number; bx: number; by: number; bz: number; nlar: THREE.Vector3[]; oplar: THREE.Vector3[]; }
    const harita = new Map<string, IKayit>();

    for (let t = 0; t < ucgen; t++) {
      const ia = idx ? idx.getX(t * 3) : t * 3;
      const ib = idx ? idx.getX(t * 3 + 1) : t * 3 + 1;
      const ic = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;
      a.fromBufferAttribute(pos, ia); b.fromBufferAttribute(pos, ib); c.fromBufferAttribute(pos, ic);
      e1.subVectors(b, a); e2.subVectors(c, a); n.crossVectors(e1, e2);
      if (n.lengthSq() < 1e-14) continue;
      n.normalize();
      const nn = n.clone();
      const noktalar = [a, b, c];
      const keyler = noktalar.map(v => `${q(v.x)},${q(v.y)},${q(v.z)}`);
      for (let k = 0; k < 3; k++) {
        const v1 = noktalar[k], v2 = noktalar[(k + 1) % 3], vop = noktalar[(k + 2) % 3];
        const ka = keyler[k], kb = keyler[(k + 1) % 3];
        const key = ka < kb ? ka + '|' + kb : kb + '|' + ka;
        let e = harita.get(key);
        if (!e) { e = { ax: v1.x, ay: v1.y, az: v1.z, bx: v2.x, by: v2.y, bz: v2.z, nlar: [], oplar: [] }; harita.set(key, e); }
        e.nlar.push(nn); e.oplar.push(vop.clone());   // bu üçgenin kenara DAHİL OLMAYAN karşı vertex'i (dışbükeylik testi için)
      }
    }

    const out: IKenarSeg[] = [];
    for (const e of harita.values()) {
      if (e.nlar.length < 2) continue;
      let n1: THREE.Vector3 | null = null, n2: THREE.Vector3 | null = null, op2: THREE.Vector3 | null = null, enDusuk = 2;
      for (let i = 0; i < e.nlar.length; i++)
        for (let j = i + 1; j < e.nlar.length; j++) {
          const d = e.nlar[i].dot(e.nlar[j]);
          if (d < enDusuk) { enDusuk = d; n1 = e.nlar[i]; n2 = e.nlar[j]; op2 = e.oplar[j]; }
        }
      if (!n1 || !n2 || !op2 || enDusuk >= cosThr) continue;
      const A = new THREE.Vector3(e.ax, e.ay, e.az);
      const B = new THREE.Vector3(e.bx, e.by, e.bz);
      const t = new THREE.Vector3().subVectors(B, A).normalize();
      // SAĞLAM dışbükeylik (n1/n2 sırasından BAĞIMSIZ): komşu yüzün karşı vertex'i n1 düzleminin
      // ALTINDAYSA (n1·(op2−A) < 0) yüzey dışa bükülür → DIŞBÜKEY. Üstündeyse içbükey.
      const disbukey = n1.dot(new THREE.Vector3().subVectors(op2, A)) < 0;
      out.push({ a: A, b: B, n1: n1.clone(), n2: n2.clone(), disbukey });
    }
    return out;
  }

  private cerceve(s: IKenarSeg): { o: THREE.Vector3; t: THREE.Vector3; uzun: number; mx: THREE.Vector3; my: THREE.Vector3 } | null {
    const t = new THREE.Vector3().subVectors(s.b, s.a);
    const uzun = t.length();
    if (uzun < 1e-5) return null;
    t.normalize();
    const n1 = s.n1.clone().addScaledVector(t, -s.n1.dot(t)).normalize();
    const n2 = s.n2.clone().addScaledVector(t, -s.n2.dot(t)).normalize();
    const mx = new THREE.Vector3().addVectors(n1, n2);
    if (mx.lengthSq() < 1e-10) return null;
    mx.normalize();
    const my = new THREE.Vector3().crossVectors(t, mx).normalize();
    return { o: s.a.clone(), t, uzun, mx, my };
  }

  private prizma(c: { o: THREE.Vector3; t: THREE.Vector3; uzun: number; mx: THREE.Vector3; my: THREE.Vector3 }, kesit: { m: number; l: number }[]): THREE.BufferGeometry | null {
    if (kesit.length < 3) return null;
    const shape = new THREE.Shape(kesit.map(p => new THREE.Vector2(p.m, p.l)));
    const pay = Math.min(0.6, c.uzun * 0.02);
    const geo = new THREE.ExtrudeGeometry(shape, { depth: c.uzun + 2 * pay, bevelEnabled: false, steps: 1 });
    const m4 = new THREE.Matrix4().makeBasis(c.mx, c.my, c.t)
      .setPosition(c.o.clone().addScaledVector(c.t, -pay));
    geo.applyMatrix4(m4);
    geo.computeVertexNormals();
    return geo;
  }

  private pahAleti(s: IKenarSeg, r: number): THREE.BufferGeometry | null {
    const c = this.cerceve(s); if (!c) return null;
    const f1 = this.yerelYon(s.n1, c);
    const f2 = this.yerelYon(s.n2, c);
    if (!f1 || !f2) return null;
    const p1 = { m: f1.m * r, l: f1.l * r };
    const p2 = { m: f2.m * r, l: f2.l * r };
    return this.prizma(c, [{ m: 0, l: 0 }, p1, p2]);
  }

  /**
   * FILLET kesiti (TEK çıkarma): köşe → yüz1 teğet noktası → YAY (içbükey, açıortay merkezli)
   * → yüz2 teğet noktası. Bu bölge çıkarılınca köşe r yarıçaplı yuvarlak yüzeyle değişir
   * (gerçek radius, pah değil). Yay merkezi açıortayda MALZEME tarafında → artık parça yok.
   */
  private filletKesit(s: IKenarSeg, r: number): THREE.BufferGeometry | null {
    const c = this.cerceve(s); if (!c) return null;
    const f1 = this.yerelYon(s.n1, c);   // yüz1 teğet birim yön (malzeme/içe)
    const f2 = this.yerelYon(s.n2, c);   // yüz2 teğet birim yön
    if (!f1 || !f2) return null;
    // teğetler arası yarı açı
    const cosA = THREE.MathUtils.clamp(f1.m * f2.m + f1.l * f2.l, -1, 1);
    const yariAci = Math.acos(cosA) / 2;
    const sinY = Math.max(0.08, Math.sin(yariAci)), tanY = Math.max(0.08, Math.tan(yariAci));
    // açıortay (içe, malzeme) → yay merkezi
    let bm = f1.m + f2.m, bl = f1.l + f2.l; const bn = Math.hypot(bm, bl) || 1; bm /= bn; bl /= bn;
    const M = { m: bm * (r / sinY), l: bl * (r / sinY) };       // yay merkezi (malzeme içinde)
    const dT = r / tanY;                                         // köşeden teğet noktası mesafesi
    const p1 = { m: f1.m * dT, l: f1.l * dT }, p2 = { m: f2.m * dT, l: f2.l * dT };
    const a1 = Math.atan2(p1.l - M.l, p1.m - M.m), a2v = Math.atan2(p2.l - M.l, p2.m - M.m);
    let d = a2v - a1; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI;
    const adim = Math.max(6, Math.ceil(Math.abs(d) / (Math.PI / 32)));
    const kesit: { m: number; l: number }[] = [{ m: 0, l: 0 }, p1];   // köşe + yüz1 teğet
    for (let i = 1; i < adim; i++) { const ang = a1 + d * (i / adim); kesit.push({ m: M.m + Math.cos(ang) * r, l: M.l + Math.sin(ang) * r }); }
    kesit.push(p2);                                                    // yüz2 teğet
    return this.prizma(c, kesit);
  }

  private yerelYon(nDunya: THREE.Vector3, c: { t: THREE.Vector3; mx: THREE.Vector3; my: THREE.Vector3 }): { m: number; l: number } | null {
    const tg = new THREE.Vector3().crossVectors(c.t, nDunya);
    if (tg.lengthSq() < 1e-10) return null;
    tg.normalize();
    let m = tg.dot(c.mx), l = tg.dot(c.my);
    if (m > 0) { m = -m; l = -l; }   // yüz teğeti İÇE (−mx, malzeme tarafı) baksın → alet köşeyi keser
    const len = Math.hypot(m, l) || 1;
    return { m: m / len, l: l / len };
  }
}
